import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import { EntitiesSupportingGroups, Group } from "@mapmycustomers/shared/types/entity";

import FrequencyEntity from "@app/component/frequency/type/FrequencyEntity";
import hasFrequency from "@app/component/frequency/util/hasFrequency";
import i18nService from "@app/config/I18nService";

import FrequencyIntervalFilter, {
  FREQUENCY_NUMBER_FILTER_OPERATORS,
} from "../../filters/FrequencyIntervalFilter";

import Field, { FieldProperties } from "./Field";

export const messages = defineMessages({
  daysCount: {
    id: "frequencyIntervalField.daysCount",
    defaultMessage:
      "{showName, select, true {{name}: } other {}}{count} {count, plural, one {day} other {days}}",
    description: "Days count for Frequency Interval Field",
  },
});

const formatFrequenciedEntity = (entity: FrequencyEntity, showName: boolean) =>
  i18nService.getIntl()?.formatMessage(messages.daysCount, {
    count: entity.cadenceInterval,
    name: entity.name,
    showName,
  }) ?? `${entity.cadenceInterval} ${(entity?.cadenceInterval ?? 0) > 1 ? " days" : "day"}`;

class FrequencyIntervalField extends Field {
  constructor(data: Omit<FieldProperties, "type">) {
    super({
      customFilterConfig: {
        defaultInstance: "frequencyIntervalFilter",
        filterInstances: {
          frequencyIntervalFilter: FrequencyIntervalFilter,
        },
        operators: [...FREQUENCY_NUMBER_FILTER_OPERATORS],
      },
      customGridProperties: {
        cellRenderer: "frequencyIntervalCellRenderer",
      },
      extraPlatformFiltersGetter: () => ({ includeGroups: true }),
      features: [FieldFeature.NON_INTEGRATION, FieldFeature.NON_IMPORT],
      type: FieldType.NUMBER,
      // The record's own cadence alone. Group cadences are a separate thing a rep can be looking
      // at: the grid and field grid draw them as their own badges, and an export composes both in
      // getFormattedCsvValueFor below.
      valueFormatter: (entity: unknown) =>
        hasFrequency(entity as FrequencyEntity)
          ? formatFrequenciedEntity(entity as FrequencyEntity, false)
          : "",
      valueGetter: (entity: unknown) => entity,
      ...data,
    });
  }

  get isMovableInLayout() {
    return true;
  }

  hasNonEmptyValueFor(entity: unknown): boolean {
    return (
      hasFrequency(entity as FrequencyEntity) ||
      !!(entity as EntitiesSupportingGroups).groups?.some(hasFrequency)
    );
  }

  getFormattedCsvValueFor(entity: unknown) {
    return [
      ...(hasFrequency(entity as FrequencyEntity)
        ? [formatFrequenciedEntity(entity as FrequencyEntity, false)]
        : []),
      ...(((entity as EntitiesSupportingGroups).groups ?? []) as Group[])
        .filter(hasFrequency)
        .map((group) => formatFrequenciedEntity(group, true)),
    ].join("; ");
    // const sequence = getFrequencySequence(
    //   entity as FrequencyEntity,
    //   ((entity as EntitiesSupportingGroups).groups ?? []) as Group[]
    // );
    // return sequence
    //   .filter(({ cadenceInterval }) => cadenceInterval)
    //   .map(
    //     ({ cadenceInterval }) =>
    //       i18nService.getIntl()?.formatMessage(messages.daysCount, { count: cadenceInterval }) ??
    //       `${cadenceInterval} ${cadenceInterval > 1 ? " days" : "day"}`
    //   )
    //   .join("; ");
  }
}

export default FrequencyIntervalField;
